/**
 * The in-memory replica and its change notifications.
 *
 * The whole dataset a user can see is small (a roster times a term of sessions), so it is
 * held in memory and every screen reads from it synchronously. That is what makes the app
 * usable on a bad connection: no screen ever waits on the network.
 */

import {
  EntityKind,
  type AttendanceMark,
  type Membership,
  type Note,
  type Person,
  type PersonId,
  type Project,
  type ProjectId,
  type SessionOverride,
  type Syncable,
} from '../../shared/types.js';
import type { PushOp, SyncChanges, SyncCursor } from '../../shared/sync-protocol.js';
import { Persistence, type CachedIdentity, type PersistedSnapshot } from './persistence.js';

/** Every replicated record the client holds, indexed by id. */
export interface Replica {
  projects: Map<ProjectId, Project>;
  memberships: Map<string, Membership>;
  people: Map<PersonId, Person>;
  sessionOverrides: Map<string, SessionOverride>;
  attendanceMarks: Map<string, AttendanceMark>;
  notes: Map<string, Note>;
}

/** Creates an empty replica. */
function emptyReplica(): Replica {
  return {
    projects: new Map(),
    memberships: new Map(),
    people: new Map(),
    sessionOverrides: new Map(),
    attendanceMarks: new Map(),
    notes: new Map(),
  };
}

/** Where replication currently stands. */
export enum SyncStatus {
  /** In step with the server as of `lastSyncedAt`. */
  Synced = 'synced',
  /** A request is in flight. */
  Syncing = 'syncing',
  /** The device is offline or the server is unreachable; local edits keep working. */
  Offline = 'offline',
  /** The server answered with an error that will not fix itself by retrying. */
  Failed = 'failed',
  /** Waiting for an authenticated session before the first sync. */
  SignedOut = 'signed_out',
}

/** Replication state shown in the UI. */
export interface SyncState {
  status: SyncStatus;
  /** Number of local writes not yet accepted by the server. */
  pending: number;
  lastSyncedAt: number | null;
  /** Human-readable description of the last failure, or `null`. */
  lastError: string | null;
}

/** Returns true when `incoming` supersedes `existing` under last-write-wins. */
function supersedes(incoming: Syncable, existing: Syncable | undefined): boolean {
  return existing === undefined || incoming.updatedAt >= existing.updatedAt;
}

/** Merges `record` into `target` when it is newer; returns whether anything changed. */
function mergeInto<T extends Syncable & { id: string }>(target: Map<string, T>, record: T): boolean {
  if (!supersedes(record, target.get(record.id))) return false;
  target.set(record.id, record);
  return true;
}

/** Generates a collision-resistant record identifier. */
export function newId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Listener invoked after any change to the replica or the sync state. */
export type ChangeListener = () => void;

/**
 * Owns the replica, the outbox, and the change notifications the UI subscribes to.
 *
 * `version` increments on every change; React components subscribe to it through
 * `useSyncExternalStore` and re-read the replica directly, which keeps the store free of
 * per-screen query plumbing.
 */
export class AppStore {
  private listeners = new Set<ChangeListener>();

  private state = { version: 0 };

  /** Live replica; read directly by selectors after a version change. */
  replica: Replica = emptyReplica();

  /** Replication cursor of the last successful pull. */
  cursor: SyncCursor = 0;

  /** Identity the replica belongs to, or `null` before the first sync. */
  identity: CachedIdentity | null = null;

  syncState: SyncState = { status: SyncStatus.SignedOut, pending: 0, lastSyncedAt: null, lastError: null };

  /** Invoked whenever a local write is queued, so the sync engine can try to deliver it. */
  onLocalWrite: (() => void) | null = null;

  /** Invoked when a screen wants fresher data than the background poll would provide. */
  onRefreshRequest: (() => void) | null = null;

  constructor(private readonly persistence: Persistence) {}

  /**
   * Asks the replication engine to run a round now.
   *
   * Opening a project is the moment stale data is most visible, so screens call this on
   * mount. It is a no-op when replication is not running.
   */
  requestRefresh(): void {
    this.onRefreshRequest?.();
  }

  /** Subscribes to change notifications; the returned function unsubscribes. */
  subscribe = (listener: ChangeListener): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  /** Returns the current version, the snapshot React compares between renders. */
  getVersion = (): number => this.state.version;

  /** Bumps the version and notifies subscribers. */
  private notify(): void {
    this.state = { version: this.state.version + 1 };
    for (const listener of this.listeners) listener();
  }

  /** Loads the persisted replica into memory and restores the outbox depth. */
  async hydrate(): Promise<void> {
    const snapshot = await this.persistence.loadAll();
    this.absorb(snapshot);
    this.syncState = { ...this.syncState, pending: snapshot.outbox.length };
    this.notify();
  }

  /** Copies a persisted snapshot into the in-memory maps. */
  private absorb(snapshot: PersistedSnapshot): void {
    this.replica = emptyReplica();
    for (const record of snapshot.projects) this.replica.projects.set(record.id, record);
    for (const record of snapshot.memberships) this.replica.memberships.set(record.id, record);
    for (const record of snapshot.people) this.replica.people.set(record.id, record);
    for (const record of snapshot.sessionOverrides) this.replica.sessionOverrides.set(record.id, record);
    for (const record of snapshot.attendanceMarks) this.replica.attendanceMarks.set(record.id, record);
    for (const record of snapshot.notes) this.replica.notes.set(record.id, record);
    this.cursor = snapshot.cursor;
    this.identity = snapshot.identity;
  }

  /** Merges one record into the replica without persisting it; returns whether it changed. */
  private merge(op: PushOp): boolean {
    switch (op.kind) {
      case EntityKind.Project: return mergeInto(this.replica.projects, op.record);
      case EntityKind.Membership: return mergeInto(this.replica.memberships, op.record);
      case EntityKind.Person: return mergeInto(this.replica.people, op.record);
      case EntityKind.SessionOverride: return mergeInto(this.replica.sessionOverrides, op.record);
      case EntityKind.AttendanceMark: return mergeInto(this.replica.attendanceMarks, op.record);
      case EntityKind.Note: return mergeInto(this.replica.notes, op.record);
    }
  }

  /**
   * Applies a local edit: updates memory immediately, then persists and queues it.
   *
   * The returned promise resolves once the write is durable. Callers may ignore it; the UI
   * is already up to date when the call returns.
   */
  async write(op: PushOp): Promise<void> {
    this.merge(op);
    this.syncState = { ...this.syncState, pending: this.syncState.pending + 1 };
    this.notify();
    await this.persistence.putRecords([op]);
    await this.persistence.enqueue(op);
    this.onLocalWrite?.();
  }

  /** Applies several local edits as one batch, for actions such as "mark everyone present". */
  async writeMany(ops: readonly PushOp[]): Promise<void> {
    if (ops.length === 0) return;
    for (const op of ops) this.merge(op);
    this.syncState = { ...this.syncState, pending: this.syncState.pending + ops.length };
    this.notify();
    await this.persistence.putRecords(ops);
    for (const op of ops) await this.persistence.enqueue(op);
    this.onLocalWrite?.();
  }

  /** Merges a pulled change set and advances the cursor. */
  async absorbChanges(changes: SyncChanges, cursor: SyncCursor): Promise<void> {
    const ops: PushOp[] = [
      ...changes.projects.map((record) => ({ kind: EntityKind.Project as const, record })),
      ...changes.memberships.map((record) => ({ kind: EntityKind.Membership as const, record })),
      ...changes.people.map((record) => ({ kind: EntityKind.Person as const, record })),
      ...changes.sessionOverrides.map((record) => ({ kind: EntityKind.SessionOverride as const, record })),
      ...changes.attendanceMarks.map((record) => ({ kind: EntityKind.AttendanceMark as const, record })),
      ...changes.notes.map((record) => ({ kind: EntityKind.Note as const, record })),
    ];
    const accepted = ops.filter((op) => this.merge(op));
    this.cursor = cursor;
    await this.persistence.putRecords(accepted);
    await this.persistence.setCursor(cursor);
    if (accepted.length > 0) this.notify();
  }

  /** Records the authenticated identity, wiping the replica when the account changed. */
  async adoptIdentity(identity: CachedIdentity): Promise<void> {
    if (this.identity !== null && this.identity.userId === identity.userId) {
      this.identity = identity;
      await this.persistence.setIdentity(identity);
      return;
    }
    if (this.identity !== null) {
      await this.persistence.clear();
      this.replica = emptyReplica();
      this.cursor = 0;
      this.syncState = { ...this.syncState, pending: 0 };
    }
    this.identity = identity;
    await this.persistence.setIdentity(identity);
    this.notify();
  }

  /**
   * Clears the replica, the outbox, and the cached identity.
   *
   * Called on sign-out so a shared device does not keep one user's roster visible to the
   * next. Queued but undelivered writes are discarded, which is why the UI warns first.
   */
  async forget(): Promise<void> {
    await this.persistence.clear();
    this.replica = emptyReplica();
    this.cursor = 0;
    this.identity = null;
    this.syncState = { status: SyncStatus.SignedOut, pending: 0, lastSyncedAt: null, lastError: null };
    this.notify();
  }

  /** Replaces the sync state and notifies subscribers. */
  setSyncState(next: Partial<SyncState>): void {
    this.syncState = { ...this.syncState, ...next };
    this.notify();
  }
}
